/**
 * Pure arithmetic behind the withdrawal settlement panel. Kept separate from the component so the
 * money rules are unit-testable without rendering anything, and so the panel stays a view.
 */

const num = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const CLASSIFICATION = {
  UNAFFECTED: "UNAFFECTED",
  SETTLEMENT: "SETTLEMENT",
  CANCELLED: "CANCELLED",
};

/** Terms before the withdrawal term are untouched, the term itself settles, everything after cancels. */
export const classifyTerms = (terms, withdrawalTermUuid) => {
  const list = Array.isArray(terms) ? terms : [];
  const index = list.findIndex((term) => term.termUuid === withdrawalTermUuid);
  return list.map((term, i) => ({
    ...term,
    classification:
      index < 0 || i < index
        ? CLASSIFICATION.UNAFFECTED
        : i === index
        ? CLASSIFICATION.SETTLEMENT
        : CLASSIFICATION.CANCELLED,
  }));
};

/** What the student ends up owing for the withdrawal term under each preset. */
export const settledAgreedFor = (mode, term, customAmount) => {
  if (!term) return 0;
  if (mode === "WAIVE_REMAINING") return Math.max(0, num(term.netPaid));
  if (mode === "CUSTOM") return Math.max(0, num(customAmount));
  return num(term.agreed);
};

/**
 * Headline figures for the summary bar. Terms net against each other, as every due total does:
 * `dueAfter` is the admission's balance when positive, `refundOwed` when negative. `profitLossImpact`
 * is the commission and bonus
 * dropped by the withdrawal, which is always a reduction and therefore never positive. Only a
 * component that counts today (claimed) moves profit/loss when dropped; a line
 * from an older server without the flag is treated as counting.
 */
export const settlementTotals = (terms, withdrawalTermUuid, mode, customAmount, retention = {}) => {
  const classified = classifyTerms(terms, withdrawalTermUuid);
  const settlementTerm = classified.find((t) => t.classification === CLASSIFICATION.SETTLEMENT);

  let balance = 0;
  let profitLossImpact = 0;

  classified.forEach((term) => {
    if (term.classification === CLASSIFICATION.UNAFFECTED) {
      balance += num(term.agreed) - num(term.netPaid);
      return;
    }

    const retained = retention[term.termUuid] || {};
    if (!retained.retainCommission && term.commissionCounted !== false) {
      profitLossImpact -= num(term.commission);
    }
    if (!retained.retainBonus && term.bonusCounted !== false) {
      profitLossImpact -= num(term.bonus);
    }

    if (term.classification === CLASSIFICATION.SETTLEMENT) {
      balance += settledAgreedFor(mode, settlementTerm, customAmount) - num(term.netPaid);
    }
  });

  return {
    dueAfter: Math.max(0, balance),
    refundOwed: Math.max(0, -balance),
    profitLossImpact,
  };
};
